import { useEffect, useRef } from 'react';

import { create } from 'zustand';

/** Action a React component exposes for the tour runner to invoke between
 *  steps (e.g. opening a drawer, expanding an accordion). Components register
 *  by string key via {@link useTourAction}; steps reference those keys in
 *  `beforeActions` on the OnboardingStep config. */
export type TourActionFn = () => void | Promise<void>;

type State = {
  actions: Record<string, TourActionFn>;
  register: (key: string, fn: TourActionFn) => void;
  unregister: (key: string) => void;
};

const useTourActionsStore = create<State>((set) => ({
  actions: {},
  register: (key, fn) => set((s) => ({ actions: { ...s.actions, [key]: fn } })),
  unregister: (key) =>
    set((s) => {
      if (!(key in s.actions)) return s;
      const next = { ...s.actions };
      delete next[key];
      return { actions: next };
    }),
}));

/** Register a tour action while the calling component is mounted. The callback
 *  is auto-unregistered on unmount, and a ref keeps the latest closure live
 *  without re-registering on every render. */
export const useTourAction = (key: string, fn: TourActionFn): void => {
  const fnRef = useRef(fn);
  fnRef.current = fn;
  const register = useTourActionsStore((s) => s.register);
  const unregister = useTourActionsStore((s) => s.unregister);
  useEffect(() => {
    register(key, () => fnRef.current());
    return () => unregister(key);
  }, [key, register, unregister]);
};

/** Hard cap so a missing action doesn't stall the tour indefinitely. */
const ACTION_WAIT_TIMEOUT_MS = 2000;

/** Run an action by key. If it isn't registered yet, wait for it (up to the
 *  timeout) — this lets us order "open the drawer" → "expand an accordion"
 *  even though the accordion's action only becomes available after the
 *  drawer mounts FilterSideBar. */
export const runTourAction = (key: string): Promise<void> =>
  new Promise<void>((resolve) => {
    let done = false;
    const finish = (fn?: TourActionFn) => {
      if (done) return;
      done = true;
      unsubscribe();
      window.clearTimeout(timeout);
      if (fn) Promise.resolve(fn()).then(() => resolve());
      else resolve();
    };
    const unsubscribe = useTourActionsStore.subscribe((s) => {
      const fn = s.actions[key];
      if (fn) finish(fn);
    });
    const timeout = window.setTimeout(() => {
      // eslint-disable-next-line no-console
      console.warn(
        `[onboarding] tour action "${key}" did not register within ${ACTION_WAIT_TIMEOUT_MS}ms — proceeding without it.`
      );
      finish(undefined);
    }, ACTION_WAIT_TIMEOUT_MS);
    // Re-check after subscribing to cover the race where the action gets
    // registered between the subscribe setup and the next state change.
    const initial = useTourActionsStore.getState().actions[key];
    if (initial) finish(initial);
  });
